/**
 * Client-side roster costing.
 *
 * This is a deliberate mirror of `packages/engine/src/roster.ts` `costRoster`,
 * and it exists for one reason: the totals bar has to move on the same frame a
 * shift lands, not on the round trip. The server still returns `totals` and it
 * remains the authority; because both sides run the same arithmetic on the same
 * inputs, the number does not flicker when the refetch arrives.
 *
 * Rules kept in step with the engine, each of which would silently skew the
 * week if it drifted:
 *   - minutes are net of the break, and an end time at or before the start is
 *     an overnight shift, not a negative one
 *   - the bucket comes from the calendar day read in UTC, so a local timezone
 *     cannot move a Saturday shift into Friday's ordinary rate
 *   - an unassigned (open) shift costs zero, because nobody is being paid for
 *     it yet. Its hours still count, so a week of open shifts reads as real
 *     work with no wage attached rather than as an empty week.
 */
import type { Shift, StaffMember, RosterTotals } from '../lib/api.ts';
import { dayIndex, shiftMinutes, type IsoDate } from '../lib/time.ts';

export type Bucket = 'ordinary' | 'saturday' | 'sunday';

export function bucketForDate(date: IsoDate): Bucket {
  const d = dayIndex(date); // 0 = Monday
  if (d === 5) return 'saturday';
  if (d === 6) return 'sunday';
  return 'ordinary';
}

const round2 = (n: number) => Math.round(n * 100) / 100;
const num = (v: string | number | null | undefined) => {
  const n = typeof v === 'string' ? Number(v) : (v ?? 0);
  return Number.isFinite(n) ? n : 0;
};

export interface Rates {
  ordinary: number;
  saturday: number;
  sunday: number;
}

export function ratesOf(staff: StaffMember[]): Record<string, Rates> {
  const out: Record<string, Rates> = {};
  for (const s of staff) {
    out[s.userId] = {
      ordinary: num(s.rateOrdinary),
      saturday: num(s.rateSaturday),
      sunday: num(s.rateSunday),
    };
  }
  return out;
}

export function shiftCost(shift: Shift, rates: Record<string, Rates>): number {
  if (!shift.userId) return 0;
  const r = rates[shift.userId];
  if (!r) return 0;
  const rate = r[bucketForDate(shift.date)];
  return round2((shiftMinutes(shift.startTime, shift.endTime, shift.breakMinutes) / 60) * rate);
}

export function costRoster(shifts: Shift[], rates: Record<string, Rates>): RosterTotals {
  const byDate: RosterTotals['byDate'] = {};
  const byUser: RosterTotals['byUser'] = {};
  let totalMinutes = 0;
  let totalCost = 0;

  for (const s of shifts) {
    const minutes = shiftMinutes(s.startTime, s.endTime, s.breakMinutes);
    const cost = shiftCost(s, rates);
    const d = byDate[s.date] ?? { minutes: 0, cost: 0 };
    byDate[s.date] = { minutes: d.minutes + minutes, cost: round2(d.cost + cost) };
    if (s.userId) {
      const u = byUser[s.userId] ?? { minutes: 0, cost: 0 };
      byUser[s.userId] = { minutes: u.minutes + minutes, cost: round2(u.cost + cost) };
    }
    totalMinutes += minutes;
    totalCost = round2(totalCost + cost);
  }
  return { byDate, byUser, totalMinutes, totalCost };
}

/** Totals for one cell (a person on a day, or an area row on a day). */
export function sumShifts(shifts: Shift[], rates: Record<string, Rates>): { minutes: number; cost: number } {
  let minutes = 0;
  let cost = 0;
  for (const s of shifts) {
    minutes += shiftMinutes(s.startTime, s.endTime, s.breakMinutes);
    cost = round2(cost + shiftCost(s, rates));
  }
  return { minutes, cost };
}

/** Fast lookup of "is this person unavailable on this date". */
export function unavailableIndex(records: Array<{ userId: string; date: string; note: string | null }>) {
  const map = new Map<string, string | null>();
  for (const r of records) map.set(`${r.userId}|${r.date}`, r.note);
  return {
    has: (userId: string | null, date: string) => (userId ? map.has(`${userId}|${date}`) : false),
    noteFor: (userId: string | null, date: string) => (userId ? (map.get(`${userId}|${date}`) ?? null) : null),
  };
}

export type UnavailableIndex = ReturnType<typeof unavailableIndex>;
